import { FocusMonitor } from '@angular/cdk/a11y';
import {
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { ErrorStateMatcher, MatOptionSelectionChange } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { InputSelectSearchItem } from '@interfaces/input-select-search-item.interface';
import { OptionSelectSearchI } from '@interfaces/option-select-search.interface';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, debounceTime, distinctUntilChanged, Subject, Subscription, take } from 'rxjs';

@Component({
  selector: 'app-input-select-search',
  templateUrl: './input-select-search.component.html',
  styleUrls: ['./input-select-search.component.scss'],
  providers: [{ provide: MatFormFieldControl, useExisting: InputSelectSearchComponent }],
})
export class InputSelectSearchComponent<TMultiple extends boolean = false>
  implements OnInit, OnDestroy, ControlValueAccessor, DoCheck, MatFormFieldControl<any>
{
  @ViewChild('select', { static: true })
  select!: MatSelect;

  @ViewChild('input', { static: true })
  input!: ElementRef<HTMLInputElement>;

  private _showAllOption: boolean = false;

  @Input() set showAllOption(value: boolean) {
    this._showAllOption = value;
  }

  get showAllOption() {
    return this._showAllOption;
  }

  @Input()
  get errorStateMatcher(): ErrorStateMatcher {
    return this.select.errorStateMatcher;
  }
  set errorStateMatcher(matcher: ErrorStateMatcher) {
    this.select.errorStateMatcher = matcher;
  }

  // @see: https://material.angular.io/guide/creating-a-custom-form-field-control#errorstate
  get errorState(): boolean {
    const control = this.ngControl?.control;

    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  @Input() allowUserInput = false;
  @Input() defaultSort: boolean = true;

  @Output() onSearchValueChanges: EventEmitter<string> = new EventEmitter();

  @ViewChild('container', { static: true })
  container!: ElementRef<HTMLDivElement>;

  @Input() value!: string;

  stateChanges = new Subject<void>();

  static nextId = 0;
  @HostBinding() id = `input-select-search-${InputSelectSearchComponent.nextId++}`;

  get shouldLabelFloat() {
    return !!this.value;
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(value) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder: string = '';

  inputFocused = false;
  selectFocused = false;
  containerFocused = false;
  @Input()
  public checkAll: boolean = false;

  get focused() {
    return this.inputFocused || this.selectFocused || this.containerFocused;
  }

  get empty() {
    return !this.value;
  }

  @Input()
  required!: boolean;

  controlType = 'input-select-search';
  autofilled?: boolean | undefined;

  describedBy?: string;
  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }
  onContainerClick(event: Event): void {
    this.select.open();

    // @hack to focus input
    setTimeout(() => {
      this.input.nativeElement.focus();
    }, 100);
  }
  @Input() label?: string;
  @Input() multiple?: TMultiple;
  @Input() hideSearch = false;
  @Input() panelClass = '';
  @Input() maxVisibleItems = 100;

  @Input() emptyOptionText: string = this.translateService.instant('NONE');
  @Input() emptyOptionEnabled: boolean = true;

  _items: InputSelectSearchItem[] = [];

  @Input() public set items(data: InputSelectSearchItem[]) {
    this._items = data;
    this.displayedItems = this.getDisplayableItems('');
  }
  get items() {
    return this._items;
  }

  @Input()
  disabled = false;
  @Input() touched = false;
  @Input() onChange: (value: string) => void = () => {};
  @Input() onTouched: () => void = () => {};

  selectedValue: any;

  public searchText = new BehaviorSubject('');
  public displayedItems: InputSelectSearchItem[] = [];

  public canShowInputData() {
    return this.allowUserInput && (this.searchText.getValue().length || !this._items.length);
  }

  public optionsList: OptionSelectSearchI[] = [];
  public clearInput = (event: MouseEvent) => {
    this.clearInputSelect(event);
  };

  #subscriptions: Subscription[] = [];
  @Input() showSelectTrigger: boolean = false;
  @Input() labelSelectTrigger: string = '';

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private focusMonitor: FocusMonitor,
    private translateService: TranslateService
  ) {
    if (this.ngControl != null) {
      // Setting the value accessor directly (instead of using
      // the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }
  }

  private allValues: string[] = [];
  ngOnInit(): void {
    const subscription = this.ngControl?.valueChanges?.subscribe((value) => {
      if (this.multiple && value?.length === 0) {
        /* clear selected if value is empty */ this.allValues = [];
      }
    });
    subscription && this.#subscriptions.push(subscription);
    this.#subscriptions.push(
      this.searchText.pipe(debounceTime(500), distinctUntilChanged()).subscribe((searchText) => {
        this.displayedItems = this.getDisplayableItems(searchText);
        this.onSearchValueChanges.emit(searchText);
        if (this.multiple && Array.isArray(this.select?.value)) {
          this.allValues = [...new Set([...this.allValues, ...(this.select?.value ?? [])])];
          this.select.writeValue(this.allValues); //keep previous selected values when searched
          this.writeValue(this.allValues);
        }
      })
    );

    this.focusMonitor
      .monitor(this.container.nativeElement)
      .pipe(take(1))
      .subscribe(() => {
        this.onTouched();
      });
    this.focusMonitor.monitor(this.container.nativeElement).subscribe((focused) => {
      this.containerFocused = !!focused;
      this.stateChanges.next();
    });

    this.focusMonitor.monitor(this.input.nativeElement).subscribe((focused) => {
      this.inputFocused = !!focused;
      this.stateChanges.next();
    });

    this.focusMonitor.monitor(this.select._elementRef.nativeElement).subscribe((focused) => {
      this.selectFocused = !!focused;
      this.stateChanges.next();
    });
  }

  ngDoCheck() {
    this.select.updateErrorState();
    this.stateChanges.next();
  }

  ngOnDestroy(): void {
    const unsubscribe = (subscription: Subscription) => subscription.unsubscribe();

    this.#subscriptions.forEach(unsubscribe);

    // stop monitoring
    // so we don't need to unsubscribe
    this.focusMonitor.stopMonitoring(this.select._elementRef);
    this.focusMonitor.stopMonitoring(this.input.nativeElement);
    this.focusMonitor.stopMonitoring(this.container.nativeElement);

    this.stateChanges.complete();
  }

  getDisplayableItems(searchText: string) {
    let currentItem: InputSelectSearchItem | null = null;

    if (this.value) {
      currentItem = this.items.find((item) => item.value === this.value) ?? null;
    }

    const isCurrentItem = (item: InputSelectSearchItem) => currentItem && item.value === currentItem.value;

    const filter = (item: InputSelectSearchItem) => {
      if (!searchText || searchText === '') {
        return !isCurrentItem(item);
      }

      return !isCurrentItem(item) && item.label.toLowerCase().includes(searchText.toLowerCase());
    };

    const maxResults = !!currentItem ? this.maxVisibleItems - 1 : this.maxVisibleItems;

    const filteredResults = maxFilteredResults(this.items, filter, maxResults);

    if (currentItem) {
      filteredResults.push(currentItem);
    }
    if (this.defaultSort) {
      return filteredResults.sort((a, b) => a.label.localeCompare(b.label));
    }
    return filteredResults;
  }

  onSelectOptionChange(isAllOption, select: MatSelect) {
    if (this.showAllOption && this.multiple) {
      if (isAllOption) {
        const items: Array<string> = select.value || [];
        this.checkOrUncheckAll(items);
        setTimeout(() => {
          this.writeValue(items);
          select.writeValue(items);
        }, 100);
      } else {
        const items: Array<string> = (this.value as any) || [];
        const len = this._items.length;
        const hasAllValues = items.length === len;
        const missingValue = items.length < len;
        if (!this.checkAll && hasAllValues) {
          //todos valores e TODOS
          this.checkAll = !this.checkAll;
        } else if (missingValue && this.checkAll) {
          this.checkAll = !this.checkAll;
          //faltando um valor e DESMARCAR TODOS
        }
      }
    }
  }
  private checkOrUncheckAll(items: Array<string>) {
    const values = items;
    if (!this.checkAll) {
      for (let i = 0; i < this._items.length; i++) {
        const item = this._items[i];
        if (!values.includes(item.value)) {
          values.push(item.value);
        }
      }
    } else if (this.checkAll) {
      while (values.length) {
        values.shift();
      }
    }
  }

  onSelectChange(event: MatSelectChange) {
    this.writeValue(event.value);
  }

  onSelectClosed() {
    !this.allowUserInput && this.searchText.next('');
  }

  onInputKeydown(event: KeyboardEvent) {
    if (event.key === ' ') {
      event.stopPropagation();
    }
  }

  public clearInputSelect(event?: MouseEvent) {
    event?.stopPropagation();
    this.searchText.next('');
    this.stateChanges.next();
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }
  writeValue(selection: any): void {
    this.value = selection;
    this.onChange?.(this.value);
    this.stateChanges.next();
  }
  registerOnChange(onChange: (selection: any) => void): void {
    this.onChange = onChange;
  }
  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }
}

function maxFilteredResults<T>(list: T[], filter: (item: T) => boolean, maxResults: number) {
  const resultList: T[] = [];

  for (const item of list[Symbol.iterator]()) {
    if (resultList.length >= maxResults) {
      break;
    }

    if (filter(item)) {
      resultList.push(item);
    }
  }

  return resultList;
}
